import { EquipmentWorkflowClient } from '@frontend/equipment/api';
import { EquipmentStateName, EquipmentTriggerName, IEquipment } from '@frontend/equipment/types';
import { Application, Feature } from '@frontend/functionality/types';
import { IoT } from '@frontend/iot/types';
import { Package, PackageStateName, PackageType } from '@frontend/package/types';
import { Slot } from '@frontend/slot/types';
import { TransactionWorkflowClient } from '@frontend/transaction/api';
import { Transaction, TransactionStateName, TransactionTriggerName, TransactionType } from '@frontend/transaction/types';

import { fetchFirstAvailableSlot } from './available-slot';

export interface LendingReturnTransactionSettings {
    transactionWorkflowId: string;
    dropOffPackageWorkflowId: string;
}

/**
 * Minimal transaction shape the helper actually reads back after `syncTransaction`.
 * Compatible with every `Transaction` variant returned by `useWizardTransaction`.
 */
export interface SyncedTransaction {
    id: string;
    account_id: string;
    user_id: string | null;
}

/**
 * Shape of the transaction sync callback that this helper needs. Compatible
 * with the `syncTransaction` returned by `useWizardTransaction`.
 */

export interface LendingReturnTransactionSyncer {
    // eslint-disable-next-line no-unused-vars
    syncTransaction: (data?: Partial<Transaction>) => Promise<SyncedTransaction>;
}

export interface SubmitLendingReturnTransactionInput {
    /** IoT with a non-null `spot_id`. The caller must validate this before calling. */
    iot: IoT & { spot_id: string };
    /** Scanned equipments that need a DROP_OFF package + slot. */
    equipments: IEquipment[];
    /** Optional user tied to the return. Required if `lostEquipments` is non-empty. */
    userId: string | null;
    /** Equipments to mark lost after submission. Lost equipment is not placed in a slot. */
    lostEquipments?: IEquipment[];
    /** Equipments to mark broken after determining their future state for slot lookup. */
    damagedEquipments?: IEquipment[];
    /** Equipments to mark incomplete after determining their future state for slot lookup. */
    incompleteEquipments?: IEquipment[];
    returnLaterEquipments?: IEquipment[];
    /**
     * Equipment IDs that may be returned into a compatible slot that already
     * contains other products. Slots containing the same product are excluded.
     */
    preferSlotWithContentForEquipmentIds?: string[];
    /** Explicit slot choices made by the user. These skip automatic slot resolution. */
    selectedSlotByEquipmentId?: Map<string, Slot>;
    /** Product id to use for slot compatibility, keyed by equipment id. */
    slotConstraintProductIdByEquipmentId?: Map<string, string>;
    /** Product ids that may not already be present in the selected slot, keyed by equipment id. */
    slotExcludedProductIdsByEquipmentId?: Map<string, string[]>;
    /** Quantity limits for excluded product ids, keyed by equipment id. */
    slotExcludedProductQuantityLimitsByEquipmentId?: Map<string, Map<string, number>>;
    settings: LendingReturnTransactionSettings;
    application?: string | null;
    feature?: string | null;
    functionality_id?: string | null;
    transactionContext: LendingReturnTransactionSyncer;
    // eslint-disable-next-line no-unused-vars
    setPackages: (packages: Partial<Package>[]) => Promise<void>;
    syncPackages: () => Promise<void>;
}

export type SubmitLendingReturnTransactionResult = { status: 'ok' } | { status: 'no-slot' };

/**
 * Shared pipeline for LENDING "return" submissions used by
 * equipment-return-lost and confirm-kit-return-slot:
 *  1. Determine future damaged equipment states, so slot lookup can use the
 *     post-trigger equipment state without mutating equipment yet.
 *  2. Resolve an available slot for every scanned equipment (in parallel).
 *  3. Apply return-later updates before any package writes.
 *  4. If at least one item was scanned, create a transaction anchored on the
 *     first scanned slot, create a DROP_OFF package per equipment, and trigger
 *     TRANSACTION_PROCESS.
 *
 * This helper is LENDING-ONLY: it hard-codes `TransactionType.LENDING` and
 * `Application.LENDING`. Returning consumables (VENDING) is not yet supported
 * and will require a separate submit helper (or this one to be generalised)
 * when that flow is implemented.
 *
 * Returns `{ status: 'no-slot' }` if any scanned equipment can't be assigned a
 * slot (the caller should surface this to the user). Throws on any other
 * failure.
 */
export const submitLendingReturnTransaction = async (input: SubmitLendingReturnTransactionInput): Promise<SubmitLendingReturnTransactionResult> => {
    const {
        iot,
        equipments,
        userId,
        lostEquipments = [],
        damagedEquipments = [],
        incompleteEquipments = [],
        returnLaterEquipments = [],
        preferSlotWithContentForEquipmentIds = [],
        selectedSlotByEquipmentId,
        slotConstraintProductIdByEquipmentId,
        slotExcludedProductIdsByEquipmentId,
        slotExcludedProductQuantityLimitsByEquipmentId,
        settings,
        application,
        feature,
        functionality_id,
        transactionContext,
        setPackages,
        syncPackages
    } = input;
    const accountId = iot.account_id;
    const spotId = iot.spot_id;
    const sharedSlotEquipmentIds = new Set(preferSlotWithContentForEquipmentIds);
    const damagedEquipmentById = new Map(damagedEquipments.map((equipment) => [equipment.id, equipment]));
    const incompleteEquipmentById = new Map(incompleteEquipments.map((equipment) => [equipment.id, equipment]));
    const lostEquipmentById = new Map(lostEquipments.map((equipment) => [equipment.id, equipment]));
    const triggerByEquipmentId = new Map<string, EquipmentTriggerName>([
        ...[...damagedEquipmentById.keys()].map((equipmentId): [string, EquipmentTriggerName] => [equipmentId, EquipmentTriggerName.EQUIPMENT_BROKEN]),
        ...[...incompleteEquipmentById.keys()].map((equipmentId): [string, EquipmentTriggerName] => [equipmentId, EquipmentTriggerName.EQUIPMENT_INCOMPLETE]),
        ...[...lostEquipmentById.keys()].map((equipmentId): [string, EquipmentTriggerName] => [equipmentId, EquipmentTriggerName.EQUIPMENT_LOST])
    ]);
    const futureStateByEquipmentId = new Map<string, EquipmentStateName>();

    await Promise.all(
        [...damagedEquipmentById.values(), ...incompleteEquipmentById.values()].map(async (equipment) => {
            const trigger = triggerByEquipmentId.get(equipment.id);
            if (!trigger) return;
            const futureEquipment = await EquipmentWorkflowClient.postDetermineFutureEquipmentState(equipment.account_id, equipment.id, {
                trigger
            });
            futureStateByEquipmentId.set(equipment.id, futureEquipment.future_state);
        })
    );

    const getEffectiveEquipmentState = (equipment: IEquipment): EquipmentStateName => {
        const futureState = futureStateByEquipmentId.get(equipment.id);
        if (futureState) return futureState;
        return equipment.state;
    };
    const effectiveEquipmentStateById = new Map<string, EquipmentStateName>(
        equipments.map((equipment) => [equipment.id, getEffectiveEquipmentState(equipment)])
    );

    // Resolve slots for every scanned equipment in parallel.
    const slotResults = await Promise.all(
        equipments.map((equipment) => {
            const isDamagedEquipment = damagedEquipmentById.has(equipment.id);
            const isIncompleteEquipment = incompleteEquipmentById.has(equipment.id);
            const effectiveEquipmentState = effectiveEquipmentStateById.get(equipment.id) ?? equipment.state;
            const isBrokenEquipment = effectiveEquipmentState === EquipmentStateName.EQUIPMENT_BROKEN;
            const selectedSlot = isDamagedEquipment || isIncompleteEquipment ? undefined : selectedSlotByEquipmentId?.get(equipment.id);
            if (selectedSlot) {
                return Promise.resolve({ equipment, slot: selectedSlot });
            }

            const preferSlotWithContent = !isDamagedEquipment && !isIncompleteEquipment && sharedSlotEquipmentIds.has(equipment.id);
            const requireEmpty =
                !preferSlotWithContent &&
                [EquipmentStateName.EQUIPMENT_LOST, EquipmentStateName.EQUIPMENT_WAITING_FOR_RETURN].includes(effectiveEquipmentState);
            const excludeSameProduct =
                isDamagedEquipment ||
                isIncompleteEquipment ||
                preferSlotWithContent ||
                (!requireEmpty && [EquipmentStateName.EQUIPMENT_IN_USE, EquipmentStateName.EQUIPMENT_WAITING_FOR_RETURN].includes(effectiveEquipmentState));
            const slotConstraintProductId = slotConstraintProductIdByEquipmentId?.get(equipment.id) ?? equipment.product_id;
            return fetchFirstAvailableSlot(
                accountId,
                spotId,
                isBrokenEquipment
                    ? {
                          account_id: accountId,
                          application,
                          feature: feature ?? Feature.RETURN_TRANSACTION,
                          functionality_id,
                          equipment_state: effectiveEquipmentState
                      }
                    : {
                          account_id: accountId,
                          application,
                          feature: feature ?? Feature.RETURN_TRANSACTION,
                          functionality_id,
                          equipment_id: equipment.id,
                          equipment_state: effectiveEquipmentState,
                          product_id: slotConstraintProductId,
                          transaction_type: TransactionType.LENDING
                      },
                {
                    preferSlotWithContent,
                    requireEmpty,
                    excludedProductId: excludeSameProduct ? equipment.product_id : null,
                    excludedProductIds: slotExcludedProductIdsByEquipmentId?.get(equipment.id),
                    excludedProductQuantityLimits: slotExcludedProductQuantityLimitsByEquipmentId?.get(equipment.id)
                }
            ).then((slot) => ({ equipment, slot }));
        })
    );

    if (slotResults.some((r) => !r.slot)) {
        return { status: 'no-slot' };
    }

    await Promise.all(
        [...damagedEquipmentById.values(), ...incompleteEquipmentById.values(), ...lostEquipmentById.values()].map(async (equipment) => {
            const trigger = triggerByEquipmentId.get(equipment.id);
            if (!trigger) return;
            await EquipmentWorkflowClient.postTriggerEquipmentState(equipment.account_id, equipment.id, { trigger });
        })
    );

    const slotByEquipmentId = new Map<string, Slot>();
    for (const { equipment, slot } of slotResults) {
        if (slot) slotByEquipmentId.set(equipment.id, slot);
    }

    // Apply remaining status changes before we create a transaction or packages
    // that would need compensation on failure.
    await Promise.all(
        returnLaterEquipments.map(async (equipment) => {
            await EquipmentWorkflowClient.updateEquipmentState(equipment.account_id, equipment.id, { state: EquipmentStateName.EQUIPMENT_WAITING_FOR_RETURN });
            await EquipmentWorkflowClient.postTriggerEquipmentState(equipment.account_id, equipment.id, {
                trigger: EquipmentTriggerName.EQUIPMENT_RETURN
            });
        })
    );

    // Pure lost/damaged flow: nothing scanned, only equipment triggers needed.
    if (equipments.length === 0) {
        return { status: 'ok' };
    }

    const primarySlot = slotByEquipmentId.get(equipments[0].id);
    if (!primarySlot) {
        // Can only happen if all `equipments` failed slot resolution, but that
        // is already covered by the `no-slot` branch above.
        return { status: 'no-slot' };
    }

    const transaction = await transactionContext.syncTransaction({
        type: TransactionType.LENDING,
        workflow_id: settings.transactionWorkflowId,
        spot_id: primarySlot.spot_id,
        module_id: primarySlot.module_id,
        slot_id: primarySlot.id,
        user_id: userId ?? undefined,
        iot_id: iot.id,
        application: Application.LENDING,
        feature: Feature.RETURN_TRANSACTION,
        state: TransactionStateName.TRANSACTION_CREATED
    });

    const newPackages: Partial<Package>[] = [];
    let priority = 1;
    for (const equipment of equipments) {
        const slot = slotByEquipmentId.get(equipment.id);
        if (!slot) continue;
        newPackages.push({
            type: PackageType.DROP_OFF,
            state: PackageStateName.PACKAGE_CREATED,
            workflow_id: settings.dropOffPackageWorkflowId,
            user_id: transaction.user_id,
            product_id: equipment.product_id,
            equipment_id: equipment.id,
            quantity: 1,
            spot_id: slot.spot_id,
            module_id: slot.module_id,
            slot_id: slot.id,
            transaction_id: transaction.id,
            account_id: transaction.account_id,
            application: Application.LENDING,
            feature: Feature.RETURN_TRANSACTION,
            priority: priority++,
            blocking: false
        });
    }

    await setPackages(newPackages);
    await syncPackages();

    await TransactionWorkflowClient.triggerTransactionState(transaction.account_id, transaction.id, {
        trigger: TransactionTriggerName.TRANSACTION_PROCESS,
        location: spotId ?? undefined,
        actor: userId ?? undefined
    });

    return { status: 'ok' };
};
